import { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { ScheduleClassDialog } from "./ScheduleClassDialog";

function getDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getAssignmentDateKey(assignment) {
  const start = assignment.start ? new Date(assignment.start) : null;

  if (!start || Number.isNaN(start.getTime())) {
    return "unknown";
  }

  return getDateKey(start);
}

function getTimeRange(assignment) {
  const start = assignment.start ? new Date(assignment.start) : null;
  const end = assignment.end ? new Date(assignment.end) : null;

  if (!start || Number.isNaN(start.getTime())) {
    return "Time unavailable";
  }

  const startText = start.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

  if (!end || Number.isNaN(end.getTime())) {
    return startText;
  }

  const endText = end.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

  return `${startText} - ${endText}`;
}

function formatDateHeader(date) {
  return date.toLocaleDateString([], {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getEventTitle(assignment) {
  return assignment.teamName || assignment.teamId || assignment.title || "Scheduled class";
}

function getJoinUrl(assignment) {
  return assignment?.joinUrl || assignment?.onlineMeetingUrl || null;
}

function groupAssignmentsByDate(assignments) {
  const groups = new Map();

  (assignments || []).forEach((assignment) => {
    const start = assignment.start ? new Date(assignment.start) : null;

    if (!start || Number.isNaN(start.getTime())) {
      return;
    }

    start.setHours(0, 0, 0, 0);
    const key = getDateKey(start);

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        date: start,
        assignments: [],
      });
    }

    groups.get(key).assignments.push(assignment);
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      assignments: group.assignments.sort(
        (a, b) => new Date(a.start) - new Date(b.start)
      ),
    }))
    .sort((a, b) => a.date - b.date);
}

export function ScheduleMobileAgendaView({ assignments }) {
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const dayGroups = useMemo(() => groupAssignmentsByDate(assignments), [assignments]);
  const [expandedDay, setExpandedDay] = useState(dayGroups[0]?.key || false);

  if (dayGroups.length === 0) {
    return (
      <Card sx={{ bgcolor: "#1A1A1A", color: "white" }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No scheduled classes were found for this range.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Tap a day to expand its scheduled classes.
      </Typography>

      <Stack gap={1.5}>
        {dayGroups.map((group) => (
          <Accordion
            key={group.key}
            expanded={expandedDay === group.key}
            onChange={(event, isExpanded) => setExpandedDay(isExpanded ? group.key : false)}
            disableGutters
            sx={{
              bgcolor: "#1A1A1A",
              color: "white",
              border: "1px solid rgba(255,255,255,0.12)",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "white" }} />}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                sx={{ width: "100%", pr: 1 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {formatDateHeader(group.date)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {group.assignments.length === 1
                      ? "1 scheduled class"
                      : `${group.assignments.length} scheduled classes`}
                  </Typography>
                </Box>
                <Chip label="Tap" size="small" />
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Stack gap={1.5}>
                {group.assignments.map((assignment) => {
                  const joinUrl = getJoinUrl(assignment);

                  return (
                    <Card
                      key={`${assignment.eventId || assignment.rawId || assignment.transactionId}-${assignment.start}`}
                      sx={{ bgcolor: "#121212", color: "white" }}
                    >
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" gap={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {getEventTitle(assignment)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {getTimeRange(assignment)}
                            </Typography>
                          </Box>
                          <Chip
                            label={assignment.online ? "Online" : "In person"}
                            size="small"
                            color={assignment.online ? "primary" : "default"}
                            sx={{ flexShrink: 0 }}
                          />
                        </Stack>

                        <Divider sx={{ my: 1.5 }} />

                        <Typography variant="body2" color="text.secondary">
                          {assignment.title || "Scheduled class"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(assignment.students || []).length === 1
                            ? "1 student listed"
                            : `${(assignment.students || []).length} students listed`}
                        </Typography>
                      </CardContent>

                      <CardActions sx={{ px: 2, pb: 2, flexWrap: "wrap", gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setSelectedAssignment(assignment)}
                        >
                          View details
                        </Button>
                        {joinUrl && (
                          <Button
                            size="small"
                            variant="contained"
                            href={joinUrl}
                            target="_blank"
                            rel="noreferrer"
                            endIcon={<OpenInNewIcon />}
                          >
                            Join Teams meeting
                          </Button>
                        )}
                        {assignment.webLink && (
                          <Button
                            size="small"
                            variant="outlined"
                            href={assignment.webLink}
                            target="_blank"
                            rel="noreferrer"
                            endIcon={<OpenInNewIcon />}
                          >
                            Open calendar event
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      <ScheduleClassDialog
        assignment={selectedAssignment}
        open={Boolean(selectedAssignment)}
        onClose={() => setSelectedAssignment(null)}
      />
    </Box>
  );
}
